// src/services/authService.js
import api from '@/core/api/api';

/**
 * Single place to decide first-login onboarding so Dashboard / Login gates stay in sync
 * with backend `compute_onboarding_flags` even when the API omits `onboarding_required`.
 */
export const deriveOnboardingRequired = (u) => {
  if (!u || typeof u !== 'object') return false;
  const role = String(u.role || u.user_type || u.user_role || '').toLowerCase();
  const managerMissingFirm =
    role === 'manager' &&
    !u.manager_firm_id &&
    !u.has_firm_profile &&
    !(u.firm && u.firm.firm_id);
  const investorMissingInstitution =
    role === 'investor' &&
    !u.institution_id &&
    !u.has_institution_profile &&
    u.agreement_accepted === true;

  return Boolean(
    u.onboarding_required === true ||
      u.needs_profile_onboarding === true ||
      u.needs_accredited_onboarding === true ||
      u.needs_billing_onboarding === true ||
      managerMissingFirm ||
      investorMissingInstitution
  );
};

/** Mirrors backend onboarding_flags accredited acceptance check. */
export const isAccreditedDeclarationAccepted = (u) => {
  if (!u || typeof u !== 'object') return false;
  const decl = u.accredited_investor_declaration;
  if (decl && typeof decl === 'object') {
    const accepted = decl.accepted;
    if (accepted === true) return true;
    if (typeof accepted === 'string' && accepted.toLowerCase() === 'true') return true;
  }
  const role = String(u.role || u.user_type || u.user_role || '').toLowerCase();
  if (role !== 'investor') return false;
  const hasInstitution = Boolean(u.institution_id || u.has_institution_profile);
  if (!hasInstitution) return false;
  // Login payload often omits the declaration blob; onboarding flag matches Dynamo acceptance.
  return u.needs_accredited_onboarding === false;
};

/**
 * Ensures a consistent user object format with snake_case keys
 * @param {Object} userData - The user data from the API
 * @returns {Object} - Standardized user object
 */
const standardizeUserFormat = (userData) => {
  // If there's no user data, return null
  if (!userData) return null;
  
  // Start with the original user data
  const standardizedUser = { ...userData };
  
  // Ensure both snake_case and camelCase versions of key properties.
  // fix: also map user_id → userId so components that read either form both work.
  if (userData.userId && !userData.user_id) {
    standardizedUser.user_id = userData.userId;
  }
  if (userData.user_id && !userData.userId) {
    standardizedUser.userId = userData.user_id;
  }
  
  if (userData.userType && !userData.user_type) {
    standardizedUser.user_type = userData.userType; 
  }
  
  if (userData.userName && !userData.user_name) {
    standardizedUser.user_name = userData.userName;
  }
  
  // Ensure both snake_case and camelCase versions of institution_type
  if (userData.institutionType && !userData.institution_type) {
    standardizedUser.institution_type = userData.institutionType;
  }
  if (userData.institution_type && !userData.institutionType) {
    standardizedUser.institutionType = userData.institution_type;
  }
  
  // Set role/user_type consistently
  if (!standardizedUser.user_type && standardizedUser.role) {
    standardizedUser.user_type = standardizedUser.role;
  }
  
  // Handle firm data for managers (new firm logo feature)
  const onboardingKeys = [
    'has_firm_profile',
    'has_institution_profile',
    'institution_id',
    'manager_firm_id',
    'needs_profile_onboarding',
    'needs_accredited_onboarding',
    'needs_billing_onboarding',
    'onboarding_required',
  ];
  onboardingKeys.forEach((k) => {
    if (userData[k] !== undefined) {
      standardizedUser[k] = userData[k];
    }
  });

  ['pending_admin_review', 'limited_access', 'approval_status'].forEach((k) => {
    if (userData[k] !== undefined) {
      standardizedUser[k] = userData[k];
    }
  });

  if (userData.firm) {
    standardizedUser.firm = userData.firm;
    
    // For backward compatibility and easier access
    if (userData.firm.logo_url) {
      standardizedUser.firmLogoUrl = userData.firm.logo_url;
    }
    if (userData.firm.firm_name) {
      standardizedUser.firmName = userData.firm.firm_name;
    }
  }

  // GET /auth/profile nests billing under firm_billing; login uses top-level flags.
  // Mirror login shape so Dashboard billing gates work after refresh (defense in depth).
  const fb = userData.firm_billing;
  if (fb && typeof fb === 'object') {
    if (fb.requires_billing_selection !== undefined) {
      standardizedUser.requires_billing_selection = fb.requires_billing_selection;
    }
    if (fb.billing_model !== undefined && standardizedUser.firm_billing_model === undefined) {
      standardizedUser.firm_billing_model = fb.billing_model;
    }
    if (
      fb.deferred_until !== undefined &&
      standardizedUser.firm_deferred_until === undefined
    ) {
      standardizedUser.firm_deferred_until = fb.deferred_until;
    }
    if (fb.access_blocked === true) {
      standardizedUser.access_blocked = true;
      standardizedUser.can_defer = false;
    } else if (fb.billing_model === 'subscription' && standardizedUser.can_defer === undefined) {
      standardizedUser.can_defer = false;
    } else if (standardizedUser.can_defer === undefined) {
      standardizedUser.can_defer = true;
    }
    if (standardizedUser.firm && typeof standardizedUser.firm === 'object') {
      const billingExtras = {};
      if (fb.billing_model !== undefined && standardizedUser.firm.firm_billing_model === undefined) {
        billingExtras.firm_billing_model = fb.billing_model;
      }
      if (fb.billing_status !== undefined && standardizedUser.firm.firm_billing_status === undefined) {
        billingExtras.firm_billing_status = fb.billing_status;
      }
      if (
        fb.deferred_until !== undefined &&
        standardizedUser.firm.firm_deferred_until === undefined
      ) {
        billingExtras.firm_deferred_until = fb.deferred_until;
      }
      if (Object.keys(billingExtras).length > 0) {
        standardizedUser.firm = { ...standardizedUser.firm, ...billingExtras };
      }
    }
  }

  standardizedUser.onboarding_required = deriveOnboardingRequired(standardizedUser);

  // Billing selection is only after admin approval; never show billing gates while pending review.
  if (standardizedUser.pending_admin_review === true) {
    standardizedUser.requires_billing_selection = false;
  }

  return standardizedUser;
};

/** Persist JWT payload from POST /auth/register (same shape as login). */
export function persistTokensFromRegistrationResponse(data) {
  if (!data || typeof data !== 'object') {
    return;
  }
  const idTok = data.token || data.idToken;
  const acc = data.accessToken || data.access_token;
  const ref = data.refreshToken || data.refresh_token;
  if (idTok) {
    localStorage.setItem('idToken', idTok);
  }
  if (acc) {
    localStorage.setItem('token', acc);
  }
  if (ref) {
    localStorage.setItem('refreshToken', ref);
  }
  const u = data.user;
  if (u && (u.userId || u.user_id)) {
    localStorage.setItem('userId', String(u.userId || u.user_id));
  } else if (data.user_id) {
    localStorage.setItem('userId', String(data.user_id));
  }
}

const authService = {
  persistTokensFromRegistrationResponse,

  // Login user with KMS-based MFA and reCAPTCHA
  login: async (email, password, recaptchaToken = null) => {
    try {
      console.log('🌐 [authService.login] Called with:', {
        email,
        hasPassword: !!password,
        recaptchaToken: {
          provided: recaptchaToken !== null && recaptchaToken !== undefined,
          type: typeof recaptchaToken,
          isTruthy: !!recaptchaToken,
          length: recaptchaToken?.length,
          preview: recaptchaToken ? recaptchaToken.substring(0, 30) + '...' : 'NULL/UNDEFINED'
        }
      });
      
      const requestBody = {
        email: email,        // Simplified field names
        password: password   // Plain text password (HTTPS encrypted)
      };
      
      // Add reCAPTCHA token if provided
      if (recaptchaToken) {
        requestBody.recaptcha_token = recaptchaToken;
        console.log('🌐 [authService.login] ✅ Added recaptcha_token to request body');
      } else {
        console.warn('🌐 [authService.login] ⚠️ recaptchaToken is falsy, NOT adding to request body!');
      }
      
      console.log('🌐 [authService.login] Final request body:', {
        email: requestBody.email,
        hasPassword: !!requestBody.password,
        hasRecaptchaToken: !!requestBody.recaptcha_token,
        recaptchaTokenLength: requestBody.recaptcha_token?.length
      });
      
      const response = await api.post(
        '/auth/login',
        requestBody
      );
      
      // Check if MFA is required
      if (response.data.mfa_required) {
        // Store MFA data in sessionStorage (temporary)
        sessionStorage.setItem('mfa_token', response.data.encrypted_token);
        sessionStorage.setItem('mfa_user_id', response.data.user_id);
        sessionStorage.setItem('mfa_expires_at', 
          Date.now() + (response.data.expires_in_minutes * 60 * 1000)
        );
        
        return {
          success: true,
          mfa_required: true,
          message: response.data.message,
          expires_in_minutes: response.data.expires_in_minutes
        };
      } else {
        // Complete login - store tokens
        if (response.data.token) {
          localStorage.setItem('idToken', response.data.token);
        }
        if (response.data.accessToken) {
          localStorage.setItem('token', response.data.accessToken);
        }
        if (response.data.refreshToken) {
          localStorage.setItem('refreshToken', response.data.refreshToken);
        }
        
        // Store user ID for token refresh (CRITICAL FIX)
        if (response.data.user && (response.data.user.userId || response.data.user.user_id)) {
          const userId = response.data.user.userId || response.data.user.user_id;
          localStorage.setItem('userId', userId);
          console.log('✅ LOGIN: Stored userId in localStorage:', userId);
        } else {
          console.error('❌ LOGIN: No userId found in response.data.user:', response.data.user);
        }
        
        // Standardize user data format
        if (response.data.user) {
          response.data.user = standardizeUserFormat(response.data.user);
        }
        
        return response.data;
      }
    } catch (error) {
      // Handle both original Axios errors and transformed error objects
      const backendMessage = error.response?.data?.error || error.details?.error;
      const backendErrorType = error.response?.data?.error || error.details?.error;
      
      // Check for pending approval status - this is not an error, it's a status message
      if (backendErrorType === 'PENDING_APPROVAL') {
        const customError = new Error(error.response?.data?.message || 'Your account is pending approval.');
        customError.type = 'PENDING_APPROVAL';
        customError.isPendingApproval = true;
        throw customError;
      }
      
      // Always use the backend message if available, otherwise use a generic fallback
      if (backendMessage) {
        throw new Error(backendMessage);
      } else {
        throw new Error('Authentication failed. Please try again.');
      }
    }
  },

  /**
   * Validate a one-time manager invite token and start the CUSTOM_AUTH OTP flow.
   * Returns { session, email } — pass session + email to verifyMagicLink() with the OTP code.
   */
  redeemInvite: async (token, userId) => {
    const response = await api.post('/auth/login/invite/redeem', { token, user_id: userId });
    return response.data; // { success, session, email, message }
  },

  requestMagicLink: async (email, recaptchaToken = null) => {
    const requestBody = { email };
    if (recaptchaToken) {
      requestBody.recaptcha_token = recaptchaToken;
    }
    const response = await api.post('/auth/login/magic-link/request', requestBody);
    return response.data;
  },

  verifyMagicLink: async (email, session, code, recaptchaToken = null) => {
    try {
      const requestBody = { email, session, code };
      if (recaptchaToken) {
        requestBody.recaptcha_token = recaptchaToken;
      }
      const response = await api.post('/auth/login/magic-link/verify', requestBody);

      if (response.data.token) {
        localStorage.setItem('idToken', response.data.token);
      }
      if (response.data.accessToken) {
        localStorage.setItem('token', response.data.accessToken);
      }
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken);
      }
      if (response.data.user && (response.data.user.userId || response.data.user.user_id)) {
        const userId = response.data.user.userId || response.data.user.user_id;
        localStorage.setItem('userId', userId);
      }
      if (response.data.user) {
        response.data.user = standardizeUserFormat(response.data.user);
      }
      return response.data;
    } catch (error) {
      const backendMessage = error.response?.data?.error || error.details?.error;
      const backendErrorType = error.response?.data?.error || error.details?.error;
      if (backendErrorType === 'PENDING_APPROVAL') {
        const customError = new Error(error.response?.data?.message || 'Your account is pending approval.');
        customError.type = 'PENDING_APPROVAL';
        customError.isPendingApproval = true;
        throw customError;
      }
      if (backendMessage) {
        throw new Error(backendMessage);
      }
      throw new Error('Authentication failed. Please try again.');
    }
  },

  // Verify MFA code
  verifyMFA: async (mfaCode) => {
    try {
      // Get MFA data from sessionStorage
      const encryptedToken = sessionStorage.getItem('mfa_token');
      const userId = sessionStorage.getItem('mfa_user_id');
      const expiresAt = sessionStorage.getItem('mfa_expires_at');
      
      if (!encryptedToken || !userId) {
        throw new Error('MFA session expired. Please login again.');
      }
      
      // Check if MFA token has expired
      if (Date.now() > parseInt(expiresAt)) {
        // Clear expired MFA data
        sessionStorage.removeItem('mfa_token');
        sessionStorage.removeItem('mfa_user_id');
        sessionStorage.removeItem('mfa_expires_at');
        throw new Error('Verification code expired. Please login again.');
      }
      
      const response = await api.post(
        '/auth/verify-mfa',
        {
          user_id: userId,
          encrypted_token: encryptedToken,
          mfa_code: mfaCode
        }
      );
      
      if (response.data.mfa_verified) {
        // Clear MFA data from sessionStorage
        sessionStorage.removeItem('mfa_token');
        sessionStorage.removeItem('mfa_user_id');
        sessionStorage.removeItem('mfa_expires_at');
        
        return {
          success: true,
          mfa_verified: true,
          message: response.data.message
        };
      } else {
        throw new Error('Invalid verification code');
      }
    } catch (error) {
      const backendMessage = error.response?.data?.error || error.details?.error;
      
      // Handle attempt tracking
      if (error.response?.data?.remaining_attempts !== undefined) {
        const remainingAttempts = error.response.data.remaining_attempts;
        if (remainingAttempts > 0) {
          throw new Error(`Invalid code. ${remainingAttempts} attempts remaining.`);
        } else {
          // Clear MFA data if no attempts left
          sessionStorage.removeItem('mfa_token');
          sessionStorage.removeItem('mfa_user_id');
          sessionStorage.removeItem('mfa_expires_at');
          throw new Error('Too many failed attempts. Please login again.');
        }
      }
      
      if (backendMessage) {
        throw new Error(backendMessage);
      } else {
        throw new Error('MFA verification failed. Please try again.');
      }
    }
  },

  // Resend MFA code
  resendMFA: async () => {
    try {
      const userId = sessionStorage.getItem('mfa_user_id');
      
      if (!userId) {
        throw new Error('No active MFA session. Please login again.');
      }
      
      // For now, we'll need to implement a resend endpoint
      // Or have the user re-login to generate a new code
      throw new Error('Please login again to receive a new verification code.');
      
    } catch (error) {
      throw new Error(error.message || 'Failed to resend verification code.');
    }
  },

  // Forgot password - send reset code
  forgotPassword: async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', {
        email: email
      });
      
      return {
        success: true,
        message: response.data.message || 'Password reset code sent to your email',
        delivery_medium: response.data.delivery_medium,
        destination: response.data.destination
      };
    } catch (error) {
      const backendMessage = error.response?.data?.error || error.details?.error;
      
      if (backendMessage) {
        throw new Error(backendMessage);
      } else {
        throw new Error('Failed to send password reset code. Please try again.');
      }
    }
  },

  // Reset password with confirmation code
  resetPassword: async (email, confirmationCode, newPassword) => {
    try {
      const response = await api.post('/auth/reset-password', {
        email: email,
        confirmation_code: confirmationCode,
        new_password: newPassword
      });
      
      return {
        success: true,
        message: response.data.message || 'Password reset successful'
      };
    } catch (error) {
      const backendMessage = error.response?.data?.error || error.details?.error;
      
      if (backendMessage) {
        throw new Error(backendMessage);
      } else {
        throw new Error('Failed to reset password. Please try again.');
      }
    }
  },

  // Send email verification code
  sendEmailVerification: async (email) => {
    try {
      const response = await api.post('/auth/send-email-verification', {
        email: email
      });
      
      return {
        success: true,
        message: response.data.message || 'Email verification code sent to your email',
        delivery_medium: response.data.delivery_medium,
        destination: response.data.destination
      };
    } catch (error) {
      const backendMessage = error.response?.data?.error || error.details?.error;
      
      if (backendMessage) {
        throw new Error(backendMessage);
      } else {
        throw new Error('Failed to send verification code. Please try again.');
      }
    }
  },

  // Verify email verification code
  verifyEmailCode: async (email, code) => {
    try {
      const response = await api.post('/auth/verify-email-code', {
        email: email,
        code: code
      });
      
      return {
        success: true,
        message: response.data.message || 'Email verified successfully',
        email_verified: response.data.email_verified,
        email_verification_token: response.data.email_verification_token ?? null,
      };
    } catch (error) {
      const backendMessage = error.response?.data?.error || error.details?.error;
      
      if (backendMessage) {
        throw new Error(backendMessage);
      } else {
        throw new Error('Failed to verify code. Please try again.');
      }
    }
  },
  
  // Check user profile/authentication status
  checkAuthStatus: async () => {
    try {
      // Send accessToken as query parameter for cognito.get_user() validation
      const accessToken = localStorage.getItem('token');
      const response = await api.get(`/auth/profile?accessToken=${encodeURIComponent(accessToken)}`);
      
      // Standardize user data format
      return standardizeUserFormat(response.data);
    } catch (error) {
      // Clear all stored tokens on auth check failure
      localStorage.removeItem('token');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      
      // Helpful error based on status code
      if (error.response && error.response.status === 401) {
        throw new Error('Session expired. Please log in again.');
      } else {
        throw new Error('Authentication check failed. Please log in again.');
      }
    }
  },
  
  // Register a new user
  register: async (userData, recaptchaToken = null) => {
    try {
      console.log('🌐 [authService.register] Called with:', {
        hasUserData: !!userData,
        role: userData?.role,
        email: userData?.email,
        recaptchaToken: {
          provided: recaptchaToken !== null && recaptchaToken !== undefined,
          type: typeof recaptchaToken,
          isTruthy: !!recaptchaToken,
          length: recaptchaToken?.length,
          preview: recaptchaToken ? recaptchaToken.substring(0, 30) + '...' : 'NULL/UNDEFINED'
        }
      });
      
      const requestBody = { ...userData };
      
      // Add reCAPTCHA token if provided
      if (recaptchaToken) {
        requestBody.recaptcha_token = recaptchaToken;
        console.log('🌐 [authService.register] ✅ Added recaptcha_token to request body');
      } else {
        console.warn('🌐 [authService.register] ⚠️ recaptchaToken is falsy, NOT adding to request body!');
      }
      
      console.log('🌐 [authService.register] Final request body summary:', {
        role: requestBody.role,
        email: requestBody.email,
        hasRecaptchaToken: !!requestBody.recaptcha_token,
        recaptchaTokenLength: requestBody.recaptcha_token?.length
      });
      
      const response = await api.post('/auth/register', requestBody);
      
      // Standardize user data format
      if (response.data.user) {
        response.data.user = standardizeUserFormat(response.data.user);
      }
      
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 409) {
        throw new Error('An account with this email already exists.');
      } else if (error.response && error.response.status === 400) {
        // Extract specific error message from backend if available
        const errorMessage = error.response.data?.error || error.response.data?.message || 'Invalid registration information. Please check your details.';
        throw new Error(errorMessage);
      } else {
        throw new Error(error.message || 'Registration failed. Please try again later.');
      }
    }
  },
  
  // Validate email (check if it exists)
  validateEmail: async (email) => {
    try {
      const response = await api.get(`/auth/validate_email?user_email=${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      console.error('Email validation error:', error);
      // Return a default response instead of throwing
      return { exists: false, is_special_domain: false };
    }
  },
  
  // Refresh access token using refresh token
  refreshToken: async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // Get user_id from localStorage or user context
      const userId = localStorage.getItem('userId') || localStorage.getItem('user_id');

      const response = await api.post('/auth/token', {
        refreshToken: refreshToken,
        user_id: userId
      });

      if (response.data && response.data.accessToken) {
        localStorage.setItem('token', response.data.accessToken);
        if (response.data.idToken) {
          localStorage.setItem('idToken', response.data.idToken);
        }
        return response.data.accessToken;
      } else {
        throw new Error('Failed to refresh token');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Clear all tokens if refresh fails
      localStorage.removeItem('token');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      throw error;
    }
  },

  // Logout - client side only as no endpoint exists
  logout: async () => {
    try {
      // You could add a server-side logout here if the API supported it
      // await api.post('/auth/logout');
      
      // Clear all tokens and user data
      localStorage.removeItem('token');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      return true;
    } catch (error) {
      // Still clear tokens even on error
      localStorage.removeItem('token');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      console.error('Logout error:', error);
      return true; // Return success anyway since we've cleared the tokens
    }
  },

  // Accept user agreements (post-login)
  acceptUserAgreements: async (agreementData) => {
    try {
      const response = await api.post('/auth/accept-agreements', agreementData);
      return response.data;
    } catch (error) {
      console.error('Error accepting agreements:', error);
      throw new Error(
        error.response?.data?.error || 
        'Failed to accept agreements. Please try again.'
      );
    }
  },

  // Upload and process registration document
  processRegistrationDocument: async ({ file_name, file_content, content_type, uploaded_by }) => {
    try {
      const response = await api.post('/manage_docs/upload/extract_firm', {
        file_name,
        file_content,
        content_type,
        uploaded_by
      });
      return response.data;
    } catch (error) {
      console.error('Document processing error:', error);
      throw new Error(
        error.response?.data?.error || 
        'Document processing failed. Please try again.'
      );
    }
  },

  // Get document processing status (for polling)
  getDocumentStatus: async (documentId) => {
    try {
      const response = await api.get(`/manage_docs/document_status/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Document status error:', error);
      throw new Error(error.response?.data?.error || 'Failed to get document status');
    }
  },

  // Process uploaded document with AI (fire and forget)
  processDocument: ({ document_id, s3_bucket, s3_key, manager_id, existing_data, document_metadata, is_last_document = false, processed_document_ids = [] }) => {
    // Fire and forget - don't await the response
    api.post('/auth/process_registration_document', {
      document_id,
      s3_bucket,
      s3_key,
      manager_id,
      existing_data,
      document_metadata,
      is_last_document,
      processed_document_ids
    }).catch(error => {
      console.error('Document processing error:', error);
      // Don't throw error since this is fire and forget
    });
    
    // Return immediately with a success indicator
    return Promise.resolve({ status: 'processing_started' });
  },

  // Get consolidated extraction results from multiple documents
  getConsolidatedExtraction: async (documentIds) => {
    try {
      const response = await api.post('/auth/get_consolidated_extraction', {
        document_ids: documentIds
      });
      return response.data;
    } catch (error) {
      console.error('Get consolidated extraction error:', error);
      throw new Error(
        error.response?.data?.error || 
        'Failed to retrieve consolidated extraction. Please try again.'
      );
    }
  }

};

export default authService;